/**
 * Wave Calculator Logic - Quotation and Budget Calculations
 * Based on Wave Calculator.xlsx from ProMedic
 */

// #765 — los umbrales 80/100, nombrados una sola vez en el árbol web.
import { BUDGET_WARNING_THRESHOLD, BUDGET_CRITICAL_THRESHOLD } from './budgetStatus';

/**
 * Round number to 2 decimal places
 * @param {number} num - Number to round
 * @returns {number} Rounded number
 */
export const round2 = (num) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

/**
 * Calculate total for a single quotation item
 * @param {number} quantity - Item quantity
 * @param {number} unitPrice - Price per unit
 * @returns {number} Total price (quantity × unit price)
 */
export const calculateItemTotal = (quantity, unitPrice) => {
  return round2(quantity * unitPrice);
};

/**
 * Calculate complete quotation totals matching Wave Calculator logic.
 *
 * PREVIEW ONLY — not the source of truth. Issue #325 found FOUR divergent
 * implementations of this formula writing `quotations.total` directly to
 * the DB (split-brain, last-writer-wins), this one among them. As of the
 * #325/#326 fix, the DB function `public.calculate_quotation_totals(uuid)`
 * is the single authoritative writer (via triggers, and via explicit RPC
 * from `quotationService.recalculateQuotationTotals`). This function is
 * now used only for the live estimate shown in `QuotationBuilder` before
 * the quotation/items exist in the DB — it must stay numerically
 * identical to the SQL function in migration
 * `20260724130000_wave_calculator_compound_formula.sql`.
 *
 * Formula (COMPOUND — issue #271; matches `Wave Calculator.xlsx` cells
 * G27=G26*1.1, G28=G27*1.3, i.e. profit compounds on the risk-adjusted
 * amount, not on the raw subtotal):
 * 1. Items subtotal = sum of quantity × unit_price for every item (no
 *    item_type filtering — issue #326)
 * 2. Risk Adjusted = Items Subtotal × (1 + risk%)
 * 3. Profit Added = Risk Adjusted × (1 + profit%)
 * 4. Base (before tax) = Profit Added
 * 5. Tax (IVA) = Base × tax%
 * 6. Grand Total = Base + Tax
 *
 * @param {Array} items - Array of quotation items with { quantity, unit_price }
 * @param {number} riskPercentage - Risk adjustment percentage (0-100)
 * @param {number} profitPercentage - Profit margin percentage (0-100)
 * @param {number} taxRate - Tax rate percentage (default 13 for IVA)
 * @returns {Object} Calculated totals breakdown
 */
export const calculateQuotationTotals = (
  items,
  riskPercentage = 0,
  profitPercentage = 0,
  taxRate = 13
) => {
  // #700 — UN NULL NO ES UN CERO, Y ACÁ SE VOLVÍA UNO SIN QUE NADIE AVISARA.
  //
  // `Math.max(0, null)` es `0`. Los parámetros por defecto sólo se aplican con
  // `undefined`, así que un `null` —que es lo que trae la columna desde #593
  // cuando nadie decidió el margen— llegaba entero hasta acá y salía convertido
  // en "sin ajuste", con `riskAmount`/`profitAmount` en 0 afirmando que el
  // ajuste se hizo. Es el gemelo web del defecto que #700 midió en el MCP.
  //
  // LA ARITMÉTICA NO CAMBIA, y es a propósito: el encabezado de esta función
  // exige paridad numérica exacta con `calculate_quotation_totals(uuid)`, que
  // hace `COALESCE(v_risk_percentage, 0)` desde #593 justamente para que el
  // borrador muestre el costo. Lo que se agrega es `marginsDefined`, para que
  // quien muestre estos números pueda decir si son un precio o el costo
  // provisional. Tirar acá tampoco serviría: `quotationService.createQuotation`
  // llama con márgenes sin definir de forma legítima (guardar un borrador en el
  // paso 1 del RACI es el estado normal, #593), y un throw rompería eso.
  const riskDefined = !isUnset(riskPercentage);
  const profitDefined = !isUnset(profitPercentage);

  // Ensure percentages are not negative
  const safeRisk = Math.max(0, riskDefined ? Number(riskPercentage) : 0);
  const safeProfit = Math.max(0, profitDefined ? Number(profitPercentage) : 0);

  // 1. Calculate items subtotal (every item, regardless of item_type)
  const itemsSubtotal = items.reduce((sum, item) => {
    return sum + (item.quantity * item.unit_price);
  }, 0);

  // 2. Risk-adjusted amount = subtotal compounded by risk%
  const riskAdjusted = itemsSubtotal * (1 + safeRisk / 100);

  // 3. Profit-added amount = risk-adjusted compounded by profit%
  const profitAdded = riskAdjusted * (1 + safeProfit / 100);

  // Breakdown amounts (for the risk/profit line items in the UI)
  const riskAmount = riskAdjusted - itemsSubtotal;
  const profitAmount = profitAdded - riskAdjusted;

  // 4. Base before tax = profit-added amount
  const baseAmount = profitAdded;

  // 5. Calculate tax (IVA)
  const taxAmount = baseAmount * (taxRate / 100);

  // 6. Grand total
  const total = baseAmount + taxAmount;

  return {
    itemsSubtotal: round2(itemsSubtotal),
    riskAmount: round2(riskAmount),
    profitAmount: round2(profitAmount),
    baseAmount: round2(baseAmount),
    taxAmount: round2(taxAmount),
    total: round2(total),
    // #700 — `false` significa: estos números son el COSTO, no el precio.
    // `QuotationBuilder` ya distingue los dos casos en pantalla (#593/#618);
    // esto le da la misma señal a cualquier otro consumidor de la función sin
    // obligarlo a re-derivarla de los porcentajes.
    marginsDefined: riskDefined && profitDefined
  };
};

/**
 * ¿A esta cotización le falta decidir el margen? (#700)
 *
 * Gemela de `margenSinDefinir` en `mcp-server/src/services/calculations.js`.
 * Toma la FILA porque todos los llamadores tienen la fila, y porque hay que
 * mirar los DOS campos: el total es compuesto y usa los dos.
 *
 * `0` NO cuenta como sin definir — cotizar a costo es una decisión válida.
 *
 * @param {Object} quotation - Fila de `quotations`
 * @returns {boolean}
 */
export const margenSinDefinir = (quotation) =>
  isUnset(quotation?.risk_percentage) || isUnset(quotation?.profit_percentage);

/**
 * #700 — el rótulo corto para un total que todavía no es un precio.
 *
 * La versión larga (para el agente) vive en `MARGEN_SIN_DEFINIR_WARNING` del
 * MCP. Ésta es para pantalla: tiene que caber al lado de un monto en una tabla.
 */
export const MARGEN_SIN_DEFINIR_LABEL =
  'Sin margen definido: este monto es el costo, no el precio. '
  + 'Falta que Administración fije el % de riesgo y el % de utilidad.';

/**
 * Format number as currency.
 *
 * `currency` NO tiene default, igual que en `src/utils/formatters.js` (#617).
 * Esta copia tenia `currency = 'CRC'` — la trampa de la otra puesta al reves,
 * que es exactamente el motivo por el que #617 se resolvio sacando el default
 * en vez de cambiarlo: quien facture en dolares con esta funcion se comia la
 * misma falla silenciosa, solo que en el otro sentido.
 *
 * Sin moneda devuelve el numero SIN simbolo: ningun camino rotula una moneda
 * que no salga de un dato.
 *
 * Tampoco mapea "cualquier cosa que no sea USD" a colones, como hacia antes
 * (`currency === 'USD' ? '$' : '₡'` convertia EUR en ₡). Un codigo que no
 * conocemos se muestra tal cual.
 *
 * @param {number} amount - Amount to format
 * @param {string} [currency] - Codigo ISO 4217 que viene del dato
 * @returns {string} Formatted currency string
 */
const CURRENCY_SYMBOLS = { CRC: '₡', USD: '$' };

export const formatCurrency = (amount, currency) => {
  const formatter = new Intl.NumberFormat('es-CR', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const formattedNumber = formatter.format(Math.abs(amount));
  const sign = amount < 0 ? '-' : '';

  if (!currency) {
    return `${sign}${formattedNumber}`;
  }

  const symbol = CURRENCY_SYMBOLS[currency];
  if (!symbol) {
    return `${sign}${currency} ${formattedNumber}`;
  }

  return `${sign}${symbol}${formattedNumber}`;
};

/**
 * Whether a percentage field was actually supplied.
 *
 * M1: `''` is what a cleared `<input type="number">` yields, and `undefined`
 * is what an omitted key yields. Neither is a zero — collapsing them into one
 * is the whole defect this validator now guards against.
 *
 * @param {*} value - Candidate percentage
 * @returns {boolean} True when nobody supplied a value
 */
const isUnset = (value) =>
  value === undefined || value === null || value === '' ||
  (typeof value === 'number' && Number.isNaN(value));

/**
 * Validate quotation data before calculation.
 *
 * M1 — **the margin is required, and an explicit zero is not the same thing as
 * no margin at all.** Measured in prod: 35 quotations, 24 of them with
 * `risk_percentage = 0 AND profit_percentage = 0`, including the two most
 * recent ones (2026-07-27). The old report blamed the web for "losing" the
 * fields; it does not — it renders them, persists them and computes with them
 * correctly. What it did was *assume* a zero nobody chose, while the MCP lane
 * declares both fields REQUIRED and tells the model, verbatim, "must come from
 * the user — never infer or assume them" (#339). Same rule, opposite policy,
 * depending on which door you came in through.
 *
 * Cuando esto se escribió (M1), ésta era la ÚNICA capa donde la distinción
 * podía vivir: sacar el `|| 0` de los servicios era necesario pero no
 * suficiente, porque la columna era `risk_percentage numeric(5,2) DEFAULT 0` y
 * omitir la clave del INSERT dejaba un cero indistinguible de uno deliberado.
 *
 * #700 — ESO YA NO ES CIERTO PARA `quotations`. #593 le hizo `DROP DEFAULT` a
 * las dos columnas de margen (migración `20260809030000`), verificado contra la
 * base local: nullable y sin default. Hoy la ausencia se guarda como NULL, o
 * sea que la distinción también vive en la BASE y no sólo acá. Sigue siendo
 * cierto palabra por palabra para `invoices`, donde las dos columnas son
 * `NOT NULL DEFAULT 0` por diseño (#L558/#629).
 *
 * Two shapes are accepted on purpose. `QuotationBuilder.validateForm` passes
 * camelCase (`riskPercentage`), but `quotationService.createQuotation` and
 * `projectQuotationService.createProjectWithQuotation` — the only two
 * call-sites that actually write to the database — pass snake_case
 * (`risk_percentage`). Under the old destructuring those two got `undefined`
 * for every percentage, so the 0-100 range check was DEAD CODE exactly where
 * it mattered: a 150% risk went straight to Postgres, to be caught (or not) by
 * the `valid_percentages` CHECK.
 *
 * @param {Object} quotationData - Quotation data to validate (camelCase or snake_case percentages)
 * @returns {Object} { isValid: boolean, errors: string[], warnings: string[] }
 */
export const validateQuotationData = (quotationData) => {
  const errors = [];
  const warnings = [];
  const { items } = quotationData;
  const riskPercentage = isUnset(quotationData.riskPercentage)
    ? quotationData.risk_percentage
    : quotationData.riskPercentage;
  const profitPercentage = isUnset(quotationData.profitPercentage)
    ? quotationData.profit_percentage
    : quotationData.profitPercentage;
  const taxRate = isUnset(quotationData.taxRate)
    ? quotationData.tax_rate
    : quotationData.taxRate;

  // Validate items array
  if (!items || items.length === 0) {
    errors.push('Quotation must have at least one item');
  } else {
    items.forEach((item, index) => {
      // Validate item type.
      //
      // #A7: esto era `if (item.item_type && ...)` — o sea, un tipo VACÍO se
      // saltaba la validación entera y llegaba al INSERT. `quotation_items`
      // tiene `item_type NOT NULL` + `quotation_items_item_type_check`
      // (material|tool|labor|admin|other), así que la fila explotaba en
      // Postgres y el usuario veía el error crudo. Peor en el tab "Nuevo
      // Proyecto": `projectQuotationService` hace rollback borrando el
      // proyecto y la cotización recién creados, así que se perdía todo el
      // formulario. El tipo es REQUERIDO acá porque lo es en la base.
      const validTypes = ['material', 'tool', 'labor', 'admin', 'other'];
      if (!item.item_type) {
        errors.push(`Item ${index + 1}: Type is required`);
      } else if (!validTypes.includes(item.item_type)) {
        errors.push(`Invalid item type: ${item.item_type}`);
      }

      // Validate required fields
      if (!item.description) {
        errors.push(`Item ${index + 1}: Description is required`);
      }
      if (!item.unit) {
        errors.push(`Item ${index + 1}: Unit is required`);
      }

      // Validate quantity.
      //
      // #A7: el CHECK vivo es `check_quantity_positive => quantity > 0`
      // (ESTRICTO, no `>= 0`). Acá se rechazaba sólo `< 0`, así que una
      // cantidad 0 — que el input permitía con `min="0"` y que
      // `parseFloat('') || 0` produce sola cuando el campo queda vacío —
      // pasaba la validación del front y moría en la base.
      if (item.quantity === undefined || item.quantity === null) {
        errors.push(`Item ${index + 1}: Quantity is required`);
      } else if (!(item.quantity > 0)) {
        errors.push('Quantity must be positive');
      }

      // Validate unit price
      if (item.unit_price === undefined || item.unit_price === null) {
        errors.push(`Item ${index + 1}: Unit price is required`);
      } else if (item.unit_price < 0) {
        errors.push(`Item ${index + 1}: Unit price must be positive`);
      }
    });
  }

  // Validate percentages.
  //
  // #593: la ausencia ya NO es un error acá. M1 la convirtió en uno, y eso
  // adelantaba al paso 1 del RACI una exigencia que el negocio pone en el paso
  // 3: el Supervisor abre la cotización, el Líder calcula ítems y recién
  // Administración agrega Utilidad % y Riesgo % antes de enviarla
  // (`wiki/pages/raci-cliente.md:45-56`). Guardar un borrador sin márgenes es
  // el estado normal de esa secuencia, no un error de captura.
  //
  // Lo que NO se relaja es la distinción: la ausencia se guarda como NULL
  // ("nadie lo definió"), nunca como 0 ("se decidió cotizar a costo"), y la
  // exigencia se mueve al envío — `validateQuotationReadyToSend` abajo, más el
  // gate de los handlers y el trigger `trg_quotation_margin_before_send`. El
  // rango sí se sigue chequeando cuando el valor viene.
  if (isUnset(riskPercentage)) {
    warnings.push(
      'Sin definir el % de riesgo: la cotización queda como borrador y no se ' +
      'podrá enviar hasta que Administración lo defina.'
    );
  } else if (riskPercentage < 0 || riskPercentage > 100) {
    errors.push('Risk percentage must be between 0 and 100');
  }
  if (isUnset(profitPercentage)) {
    warnings.push(
      'Sin definir el % de utilidad: la cotización queda como borrador y no se ' +
      'podrá enviar hasta que Administración lo defina.'
    );
  } else if (profitPercentage < 0 || profitPercentage > 100) {
    errors.push('Profit percentage must be between 0 and 100');
  }
  // The tax rate keeps its old semantics on purpose: it DOES have a
  // company-wide default (13% IVA), so its absence is legitimate and
  // `DEFAULT_TAX_RATE` fills it in downstream. The margin has no such default
  // — that is precisely why it cannot be defaulted. Only the range is checked.
  if (!isUnset(taxRate) && (taxRate < 0 || taxRate > 100)) {
    errors.push('Tax rate must be between 0 and 100');
  }

  // A deliberate zero margin is VALID — quoting at cost is a commercial
  // decision somebody is allowed to make. It is still worth saying out loud,
  // so callers can surface it instead of shipping it silently.
  if (
    !isUnset(riskPercentage) && !isUnset(profitPercentage) &&
    Number(riskPercentage) === 0 && Number(profitPercentage) === 0
  ) {
    warnings.push(
      'Sin margen: esta cotización se emite a costo (0% de riesgo y 0% de ganancia).'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * #593 — La compuerta del carril web: ¿esta cotización puede salir?
 *
 * Gemela de `assertMarginDecided` en
 * `mcp-server/src/tools/quotationTools.js`. Es donde vive ahora el refuse de
 * #339: no al crear (paso 1 del RACI, el Supervisor, antes de que existan los
 * ítems) sino al aprobar/enviar (pasos 3 y 4, Administración). La misma
 * protección, en el punto del proceso donde la respuesta existe.
 *
 * Un 0 deliberado pasa: cotizar a costo es una decisión que alguien puede
 * tomar. NULL no pasa: eso es que nadie decidió.
 *
 * @param {Object} quotation - Fila de la cotización (risk_percentage / profit_percentage)
 * @returns {{isValid: boolean, missing: string[], message: string|null}}
 */
export const validateQuotationReadyToSend = (quotation) => {
  const missing = [];
  if (isUnset(quotation?.risk_percentage)) missing.push('el % de riesgo');
  if (isUnset(quotation?.profit_percentage)) missing.push('el % de utilidad');

  if (missing.length === 0) {
    return { isValid: true, missing: [], message: null };
  }

  return {
    isValid: false,
    missing,
    message:
      `Falta ${missing.join(' y ')}. Es una decisión comercial que pone ` +
      'Administración antes de enviar la cotización (paso 3 del RACI): no hay ' +
      'valor estándar de ProMedic ni rango típico. Un 0 explícito es válido si ' +
      'se decidió cotizar a costo; lo que no vale es dejarlo sin definir.'
  };
};

/**
 * Calculate budget variance and status.
 *
 * Mirrors `mcp-server/src/services/calculations.js#calculateBudgetVariance`
 * (issue #469): same no-budget guard, same status vocabulary, same field
 * name (`variancePercentage`). The two lanes must stay numerically and
 * nominally identical — see the Dual-Lane Parity rule in CLAUDE.md.
 *
 * @param {number} quotedAmount - Quoted/budgeted amount
 * @param {number} spentAmount - Amount spent (plus committed) so far
 * @returns {{variance: number, variancePercentage: number, remaining: number,
 *   status: 'no_budget'|'ok'|'warning'|'critical', isOverBudget: boolean}}
 */
export const calculateBudgetVariance = (quotedAmount, spentAmount) => {
  const variance = spentAmount - quotedAmount;

  // #469: a project with no budget set is NOT a budget overrun — there is no
  // baseline to exceed yet. This used to return Infinity, which fell straight
  // through the `>= 100` check below and reported every budget-less project as
  // `critical` (red "presupuesto excedido"), for example any project created
  // before its quotation is approved. The MCP lane already returned
  // `no_budget` here; the web lane now agrees.
  if (!quotedAmount || quotedAmount <= 0) {
    return {
      // The raw arithmetic is kept (it still answers "how much was spent with
      // no budget set"), but nothing derived from a ratio is invented.
      variance: round2(variance),
      // #679/#685: estos dos eran 0 y ese 0 se leía como hecho —
      // "0% consumido, vas bien" y "no te queda nada" sobre un proyecto que
      // simplemente todavía no tiene presupuesto. `null` = no calculable.
      variancePercentage: null,
      remaining: null,
      status: 'no_budget',
      isOverBudget: false
    };
  }

  const variancePercentage = round2((spentAmount / quotedAmount) * 100);

  let status = 'ok';
  if (variancePercentage >= BUDGET_CRITICAL_THRESHOLD) {
    status = 'critical';
  } else if (variancePercentage >= BUDGET_WARNING_THRESHOLD) {
    status = 'warning';
  }

  return {
    variance: round2(variance),
    variancePercentage,
    remaining: round2(quotedAmount - spentAmount),
    status,
    isOverBudget: spentAmount > quotedAmount
  };
};

/**
 * Whether an inventory item counts as "bajo stock".
 *
 * DUAL-LANE PARITY (#507) — web mirror of `isLowStock()` in
 * `mcp-server/src/services/calculations.js`, which both MCP lanes already use
 * (`tools/inventoryTools.js` and the stdio `mcp-server.js`).
 *
 * The comparison is `<=`, NOT `<`: an item sitting exactly AT its minimum
 * already counts as low. The web lane used to spell this inline as
 * `current_stock < min_stock_level` in four separate places, so on the boundary
 * row the UI said "OK" while the agent said "bajo stock" — same item, two
 * answers depending on which door you came in through.
 *
 * Keep this expression equivalent to the MCP one. Do NOT "improve" one side
 * with null-guards or coercion: divergence between two copies of this rule is
 * the bug, not the strictness of either copy.
 *
 * @param {number} currentStock - Current stock on hand
 * @param {number} minStockLevel - Minimum stock level configured for the item
 * @returns {boolean} True when stock is at or below the minimum
 */
export const isLowStock = (currentStock, minStockLevel) => {
  return currentStock <= minStockLevel;
};

/**
 * Generate project number
 * @param {number} sequence - Sequential number
 * @param {Date} date - Date for MMDD suffix
 * @returns {string} Project number in format Q###EQ-SL-MMDD
 */
export const generateProjectNumber = (sequence, date = new Date()) => {
  const seq = String(sequence).padStart(3, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `Q${seq}EQ-SL-${month}${day}`;
};

/**
 * Generate quotation number
 * @param {string} projectNumber - Project number
 * @param {number} sequence - Quotation sequence for this project
 * @returns {string} Quotation number in format Q###-###
 */
export const generateQuotationNumber = (projectNumber, sequence = 1) => {
  const projectPart = projectNumber.split('EQ')[0]; // Get Q### part
  const seqPart = String(sequence).padStart(3, '0');
  return `${projectPart}-${seqPart}`;
};

const calculations = {
  round2,
  calculateItemTotal,
  calculateQuotationTotals,
  formatCurrency,
  validateQuotationData,
  calculateBudgetVariance,
  isLowStock,
  generateProjectNumber,
  generateQuotationNumber,
  margenSinDefinir,
  MARGEN_SIN_DEFINIR_LABEL
};

export default calculations;
